import { memo, useCallback, useRef } from "react";
import { Keyboard, Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import Animated, { FadeIn, FadeOut } from "react-native-reanimated";
import { FormInput, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { Text } from "@src/components/Text";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { CamerasTabHeaderActions } from "./CamerasTabHeaderActions.native";
import { CameraSearchSheet } from "./CameraSearchSheet.native";
import { styles } from "./CamerasTabHeader.native";
import type { FC } from "react";
import type { CamerasHeaderProps } from "./CamerasTabHeader.native";
import type { CameraSearchSheetHandle } from "./CameraSearchSheet.native";

export const CamerasTabLegacyHeader: FC<CamerasHeaderProps> = memo(
  ({ tenantDisplayName, tenantsCount, onTenantPress, ...sharedProps }) => {
    const { theme } = useUnistyles();
    const isCrossSearchEnabled = useBooleanFlag(FEATURE_FLAGS.CAMERA_SEARCH);
    const searchSheetRef = useRef<CameraSearchSheetHandle>(null);

    const handleSearchPress = useCallback(() => {
      if (isCrossSearchEnabled) {
        searchSheetRef.current?.present();
      } else {
        sharedProps.onSearchPress();
      }
    }, [isCrossSearchEnabled, sharedProps]);

    const tenantA11y =
      tenantDisplayName ?
        tenantsCount > 0 ?
          `${tenantDisplayName}, ${tenantsCount} ${tenantsCount === 1 ? "organisation" : "organisations"}. Tap to change.`
        : `${tenantDisplayName}. Tap to change.`
      : "Select organisation";

    return (
      <>
        <View style={[styles.toolbar, styles.legacyToolbar]}>
          <View style={[styles.toolbarRow, styles.legacyToolbarRow]}>
            <View style={styles.tenantGroup}>
              <Pressable
                style={styles.iconLeading}
                onPress={onTenantPress}
                accessibilityRole="button"
                accessibilityLabel="Switch organisation"
              >
                <Icon name="settingsOutline" size={24} color={theme.colors.textMuted} />
              </Pressable>
              <Pressable
                style={styles.tenantSection}
                onPress={onTenantPress}
                accessibilityRole="button"
                accessibilityLabel={tenantA11y}
              >
                <View flex={1} minWidth={0}>
                  <Text textStyle="sm/medium" numberOfLines={1}>
                    {tenantDisplayName ?? "Select organisation"}
                  </Text>
                  {tenantsCount > 0 && (
                    <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                      {tenantsCount} {tenantsCount === 1 ? "organisation" : "organisations"}
                    </Text>
                  )}
                </View>
              </Pressable>
            </View>
            <CamerasTabHeaderActions
              onSearchPress={handleSearchPress}
              variant="legacy"
              hasAlert={isCrossSearchEnabled && !!sharedProps.searchQuery}
            />
          </View>

          {!isCrossSearchEnabled && sharedProps.searchExpanded && (
            <Animated.View style={styles.searchBarWrap} entering={FadeIn} exiting={FadeOut}>
              <FormInput
                ref={sharedProps.searchInputRef}
                placeholder="Search cameras"
                value={sharedProps.searchQuery}
                onChangeText={sharedProps.onSearchQueryChange}
                size="small"
                returnKeyType="search"
                onSubmitEditing={() => Keyboard.dismiss()}
                style={styles.searchInput}
                iconLeft={<Icon name="search" size={20} color={theme.colors.textMuted} />}
                iconRight={
                  sharedProps.searchQuery ?
                    <Pressable
                      onPress={() => sharedProps.onSearchQueryChange("")}
                      accessibilityRole="button"
                      accessibilityLabel="Clear search"
                    >
                      <Icon name="closeCircle" size={20} color={theme.colors.textMuted} />
                    </Pressable>
                  : undefined
                }
              />
            </Animated.View>
          )}
        </View>

        {isCrossSearchEnabled && (
          <CameraSearchSheet
            ref={searchSheetRef}
            searchQuery={sharedProps.searchQuery}
            onSearchQueryChange={sharedProps.onSearchQueryChange}
          />
        )}
      </>
    );
  },
);

CamerasTabLegacyHeader.displayName = "CamerasTabLegacyHeader";
